/**
 * Custom field helpers for candidate reports.
 *
 * The report renders one column per organization custom field that actually
 * holds data, so an org with no custom fields — or fields nobody has filled in
 * — sees no change at all. Column order follows the definition's display_order.
 */

import { BaseCandidate, CandidateCustomFieldValue } from './types';

/**
 * Anything carrying custom field values — a candidate, or a ballot initiative
 * report row (PRD v2 §5.17.3). Definitions and values are the same table under
 * a different entity type, so the display rules are shared, not copied.
 */
export type CustomFieldBearer = Pick<BaseCandidate, 'customFieldValues'>;

export interface CandidateCustomFieldColumn {
  definitionId: string;
  name: string;
}

/** Raw snake_case shape as returned by the report endpoints. */
interface RawCustomFieldValue {
  definition_id?: string;
  name?: string;
  field_type?: string;
  field_config?: Record<string, unknown> | null;
  display_order?: number | string | null;
  workspace_id?: string;
  workspace_name?: string | null;
  value?: unknown;
}

/** Map backend rows to the camelCase shape used across the candidate components. */
export function normalizeCustomFieldValues(raw: unknown): CandidateCustomFieldValue[] {
  if (!Array.isArray(raw)) return [];
  return (raw as RawCustomFieldValue[])
    .filter((entry) => entry && entry.definition_id)
    .map((entry) => ({
      definitionId: String(entry.definition_id),
      name: entry.name || 'Custom Field',
      fieldType: (entry.field_type || 'text') as CandidateCustomFieldValue['fieldType'],
      fieldConfig: (entry.field_config || undefined) as CandidateCustomFieldValue['fieldConfig'],
      displayOrder:
        typeof entry.display_order === 'string'
          ? parseInt(entry.display_order, 10) || 0
          : entry.display_order ?? 0,
      workspaceId: entry.workspace_id,
      workspaceName: entry.workspace_name ?? null,
      value: entry.value,
    }));
}

/**
 * Strip markup from richtext so a value is safe and readable inside a table
 * cell. Report views (including public ones) render text, never raw HTML.
 */
function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Render a stored custom field value as display text.
 * Returns '' when there is nothing to show, so callers can fall back to a dash.
 */
export function formatCustomFieldValue(entry: CandidateCustomFieldValue): string {
  const { value, fieldType, fieldConfig } = entry;

  if (value === null || value === undefined || value === '') return '';

  switch (fieldType) {
    case 'boolean':
      return value ? 'Yes' : 'No';

    case 'date': {
      const parsed = new Date(String(value));
      return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
    }

    case 'currency': {
      const numeric = typeof value === 'number' ? value : parseFloat(String(value));
      if (Number.isNaN(numeric)) return String(value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: fieldConfig?.currency_code || 'USD',
        minimumFractionDigits: fieldConfig?.decimal_places ?? 2,
        maximumFractionDigits: fieldConfig?.decimal_places ?? 2,
      }).format(numeric);
    }

    case 'number': {
      const numeric = typeof value === 'number' ? value : parseFloat(String(value));
      if (Number.isNaN(numeric)) return String(value);
      const formatted = numeric.toLocaleString();
      return `${fieldConfig?.prefix || ''}${formatted}${fieldConfig?.suffix || ''}`;
    }

    case 'multi_select':
      return Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value);

    case 'richtext':
      return stripHtml(String(value));

    default:
      return String(value);
  }
}

/**
 * The custom field columns to render: every field with at least one non-empty
 * value across the supplied candidates, ordered by display_order then name.
 */
export function collectCustomFieldColumns(candidates: CustomFieldBearer[]): CandidateCustomFieldColumn[] {
  const seen = new Map<string, { name: string; displayOrder: number }>();

  for (const candidate of candidates) {
    for (const entry of candidate.customFieldValues || []) {
      if (!formatCustomFieldValue(entry)) continue;
      if (!seen.has(entry.definitionId)) {
        seen.set(entry.definitionId, {
          name: entry.name,
          displayOrder: entry.displayOrder ?? 0,
        });
      }
    }
  }

  return Array.from(seen.entries())
    .sort(([, a], [, b]) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
    .map(([definitionId, meta]) => ({ definitionId, name: meta.name }));
}

export interface CandidateCustomFieldDisplay {
  text: string;
  /** Set only when workspaces disagree, to say which value came from where. */
  workspaceName?: string | null;
}

/**
 * Display entries for one field on one candidate.
 *
 * Values are workspace-scoped, so an org report can hold several. Identical
 * values collapse to a single unlabelled entry; genuinely different values are
 * each labelled with their workspace rather than silently picking a winner.
 */
export function getCustomFieldDisplayValues(
  candidate: CustomFieldBearer,
  definitionId: string
): CandidateCustomFieldDisplay[] {
  const entries = (candidate.customFieldValues || []).filter(
    (entry) => entry.definitionId === definitionId
  );
  if (entries.length === 0) return [];

  const byText = new Map<string, (string | null | undefined)[]>();
  for (const entry of entries) {
    const text = formatCustomFieldValue(entry);
    if (!text) continue;
    if (!byText.has(text)) byText.set(text, []);
    byText.get(text)!.push(entry.workspaceName);
  }

  if (byText.size === 0) return [];
  if (byText.size === 1) {
    return [{ text: Array.from(byText.keys())[0] }];
  }

  return Array.from(byText.entries()).map(([text, workspaceNames]) => ({
    text,
    workspaceName: workspaceNames.filter(Boolean).join(', ') || null,
  }));
}

/** Flatten one field to a single string, for exports and tooltips. */
export function getCustomFieldExportText(candidate: CustomFieldBearer, definitionId: string): string {
  return getCustomFieldDisplayValues(candidate, definitionId)
    .map((display) => (display.workspaceName ? `${display.text} (${display.workspaceName})` : display.text))
    .join('; ');
}
